import React, { useState, useRef, useEffect } from "react";
import Image from "next/image";
import styles from "./RegulatoryComplianceSustainability.module.scss";
import OSHOFDASVG from "./OSHOFDASVG";
import USBasedSVG from "./USBasedSVG";
import UpToDateSVG from "./UpToDateSVG";
import EndToEndSVG from "./EndToEndSVG";
import EthicallySourcedSVG from "./EthicallySourcedSVG";
import LowImpactSVG from "./LowImpactSVG";
import EnergyEfficientSVG from "./EnergyEfficientSVG";
import TransparentLifecycleSVG from "./TransparentLifecycleSVG";
import { useRouter } from "next/router";
import { getCDNImageUrl } from "@/utils/Common";

const IconInfo = ({ icon, description }) => {
  return (
    <div className={styles.iconInfo}>
      {/* Icon */}
      <div className={styles.icon}>{icon()}</div>
      {/* Description */}
      <p>{description}</p>
    </div>
  );
};

const RegulatoryComplianceIconInfos = [
  {
    id: 1,
    icon: OSHOFDASVG,
    description: "OSHA, FDA, and EPA aligned documentation",
  },
  { id: 2, icon: USBasedSVG, description: "US based regulatory experts" },
  {
    id: 3,
    icon: UpToDateSVG,
    description: "Up-to-date CoAs, SDS, and import documentation",
  },
  { id: 4, icon: EndToEndSVG, description: "End-to-end traceability" },
];

const SustainabilityIconInfos = [
  {
    id: 1,
    icon: EthicallySourcedSVG,
    description: "Ethically sourced ingredients",
  },
  { id: 2, icon: LowImpactSVG, description: "Low-impact shipping routes" },
  {
    id: 3,
    icon: EnergyEfficientSVG,
    description: "Energy-efficient manufacturing partners",
  },
  {
    id: 4,
    icon: TransparentLifecycleSVG,
    description: "Transparent lifecycle tracking",
  },
];

function RegulatoryComplianceSustainability() {
  const [selectedTab, setSelectedTab] = useState("Regulatory Compliance");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const tabRefs = useRef({});
  const router = useRouter();
  const onClickLearnMore = () => {
    router.push("/sustainability-elchemy");
  };

  const tabs = [
    {
      id: "regulatory",
      label: "Regulatory Compliance",
      value: "Regulatory Compliance",
    },
    { id: "sustainability", label: "Sustainability", value: "Sustainability" },
  ];

  const updateIndicatorPosition = (tabValue) => {
    const selectedTabData = tabs.find((tab) => tab.value === tabValue);
    if (selectedTabData && tabRefs.current[selectedTabData.id]) {
      const tabElement = tabRefs.current[selectedTabData.id];
      const { offsetLeft, offsetWidth } = tabElement;

      setIndicatorStyle({
        transform: `translateX(${offsetLeft}px)`,
        width: `${offsetWidth}px`,
      });
    }
  };

  useEffect(() => {
    // Update indicator position when component mounts or selectedTab changes
    updateIndicatorPosition(selectedTab);
  }, [selectedTab]);

  useEffect(() => {
    // Handle window resize
    const handleResize = () => {
      updateIndicatorPosition(selectedTab);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [selectedTab]);

  const handleTabClick = (tabValue) => {
    if (tabValue === selectedTab || isTransitioning) return;

    setIsTransitioning(true);

    // Wait for fade out animation to complete, then switch content and fade in
    setTimeout(() => {
      setSelectedTab(tabValue);
      setIsTransitioning(false);
    }, 300); // Match the CSS transition duration
  };

  return (
    <div className={styles.regulatoryComplianceSustainability}>
      {/* Tabs */}
      <div className={styles.tabsContainer}>
        {tabs.map((tab, index) => (
          <div
            key={tab.id}
            ref={(el) => (tabRefs.current[tab.id] = el)}
            className={`${styles.tab} ${
              selectedTab === tab.value ? styles.active : ""
            }`}
            onClick={() => handleTabClick(tab.value)}
          >
            {tab.label}
          </div>
        ))}
        {/* Moving Indicator */}
        <div className={styles.indicator} style={indicatorStyle} />
      </div>

      {/* Active Selection - Regulatory Compliance / Sustainability */}
      {/* Regulatory Compliance Section */}
      {selectedTab === "Regulatory Compliance" && (
        <div
          className={`${styles.contentSection} ${
            isTransitioning ? styles.fadeOut : styles.fadeIn
          }`}
        >
          {/* Left Side Content */}
          <div className={styles.leftSideContent}>
            {/* Title */}
            <h3>Compliance You Can Count On</h3>
            {/* Description */}
            <p>
              Navigating chemical regulations across borders can be complex, but
              we make it simple. With a dedicated compliance team and
              documentation tailored for the US market, Elchemy ensures every
              product meets rigorous safety, quality, and regulatory
              standards.We help keep your operations audit-ready,
              well-documented and worry-free.
            </p>
            {/* Mobile Icons Row */}
            <div className={styles.mobileIconsRow}>
              <div className={styles.mobileIcon}>
                <div className={styles.imageWrapper}>
                  <Image
                    src={getCDNImageUrl("OSHA.webp")}
                    alt="OSHA"
                    fill
                    className={styles.image}
                  />
                </div>
              </div>
              <div className={styles.mobileIcon}>
                <div className={styles.imageWrapper}>
                  <Image
                    src={getCDNImageUrl("EPA.webp")}
                    alt="US Environment"
                    fill
                    className={styles.image}
                  />
                </div>
              </div>
              <div className={styles.mobileIcon}>
                <div className={styles.imageWrapper}>
                  <Image
                    src={getCDNImageUrl("FDA2.webp")}
                    alt="FDA"
                    fill
                    className={styles.image}
                  />
                </div>
              </div>
            </div>
            {/* Icon-Info Row-1 */}
            <div className={styles.iconInfoRow}>
              <IconInfo
                icon={RegulatoryComplianceIconInfos[0].icon}
                description={RegulatoryComplianceIconInfos[0].description}
              />
              <IconInfo
                icon={RegulatoryComplianceIconInfos[1].icon}
                description={RegulatoryComplianceIconInfos[1].description}
              />
            </div>
            {/* Icon-Info Row-2 */}
            <div className={styles.iconInfoRow}>
              <IconInfo
                icon={RegulatoryComplianceIconInfos[2].icon}
                description={RegulatoryComplianceIconInfos[2].description}
              />
              <IconInfo
                icon={RegulatoryComplianceIconInfos[3].icon}
                description={RegulatoryComplianceIconInfos[3].description}
              />
            </div>
            {/* Explore Button */}
            <button onClick={onClickLearnMore}>Explore</button>
          </div>
          {/* Right Side Content */}
          <div className={styles.imageContainer}>
            <Image
              src={getCDNImageUrl("Regulatory-Large.webp")}
              alt="Regulatory Compliance"
              fill
              className={styles.image}
            />
          </div>
        </div>
      )}

      {/* Sustainability Section */}
      {selectedTab === "Sustainability" && (
        <div
          className={`${styles.contentSection} ${
            isTransitioning ? styles.fadeOut : styles.fadeIn
          }`}
        >
          {/* Left Side Content */}
          <div className={styles.leftSideContent}>
            {/* Title */}
            <h3>Chemistry That Respects the Planet</h3>
            {/* Description */}
            <p>
              At Elchemy, sustainability isn't an add-on - it's a commitment
              woven into every stage of our supply chain. From sourcing
              environmentally responsible raw materials to minimizing carbon
              emissions through strategic warehousing and smart logistics, we
              prioritize planet-first practices without compromising product
              quality or efficiency.
            </p>
            {/* Mobile Image */}
            <img
              className={styles.mobileImage}
              src={getCDNImageUrl("Sustainability.webp")}
              alt="Sustainability"
              objectFit="cover"
              objectPosition="center"
            />
            {/* Icon-Info Row-1 */}
            <div className={styles.iconInfoRow}>
              <IconInfo
                icon={SustainabilityIconInfos[0].icon}
                description={SustainabilityIconInfos[0].description}
              />
              <IconInfo
                icon={SustainabilityIconInfos[1].icon}
                description={SustainabilityIconInfos[1].description}
              />
            </div>
            {/* Icon-Info Row-2 */}
            <div className={styles.iconInfoRow}>
              <IconInfo
                icon={SustainabilityIconInfos[2].icon}
                description={SustainabilityIconInfos[2].description}
              />
              <IconInfo
                icon={SustainabilityIconInfos[3].icon}
                description={SustainabilityIconInfos[3].description}
              />
            </div>
            {/* Learn More Button */}
            <button onClick={onClickLearnMore}>Explore</button>
          </div>
          {/* Right Side Content */}
          <div className={styles.imageContainer}>
            <Image
              src={getCDNImageUrl("Sustainability-Large.webp")}
              alt="Sustainability"
              fill
              className={styles.image}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default RegulatoryComplianceSustainability;
